import React, { useState, useEffect } from 'react';
import {
  DeleteOutlined,
  ClearOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DoubleLeftOutlined,
  LeftOutlined,
  RightOutlined,
  DoubleRightOutlined,
  PlusOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { usersAPI } from '../services/api';
import './SearchesPanel.css';

const SearchesPanel = ({
  searches,
  selectedSearch,
  onSearchSelect,
  onSearchDelete,
  onSearchClear,
  onNewSearch,
  collapsed,
  onToggleCollapse,
  filterType,
  onFilterTypeChange,
  filterStatus,
  onFilterStatusChange,
  filterUser,
  onFilterUserChange,
  isAdmin,
  onRefresh,
  onTogglePause,
  onPauseAll,
  onDeleteAll,
  onDownloadAllUnopened,
  currentPage,
  totalPages,
  totalSearches,
  onPageChange,
  searchText,
  onSearchTextChange,
}) => {
  const [users, setUsers] = useState([]);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.list();
      setUsers(response.data.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const getStatusBadgeClass = (state) => {
    switch (state) {
      case 'new':
        return 'badge-secondary';
      case 'in_progress':
        return 'badge-running';
      case 'done':
        return 'badge-success';
      case 'failed':
        return 'badge-danger';
      case 'opened':
        return 'badge-opened';
      case 'cleared':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  };

  const getStatusDisplayText = (state) => {
    switch (state) {
      case 'new':
        return 'New';
      case 'in_progress':
        return 'In Progress';
      case 'done':
        return 'Done';
      case 'failed':
        return 'Failed';
      case 'opened':
        return 'Opened';
      case 'cleared':
        return 'Cleared';
      default:
        return state;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (collapsed) {
    return (
      <div className="searches-panel collapsed">
        <button
          className="toggle-btn"
          onClick={onToggleCollapse}
          title="Expand panel"
        >
          <RightOutlined />
        </button>
      </div>
    );
  }

  return (
    <div className="searches-panel">
      <div className="panel-header">
        <h3>Searches</h3>
        <div className="header-actions">
          <button
            className="icon-btn"
            onClick={onNewSearch}
            title="New Search"
          >
            <PlusOutlined />
          </button>
          <button
            className="icon-btn"
            onClick={() => onRefresh()}
            title="Refresh"
          >
            <ReloadOutlined />
          </button>
          <button
            className="toggle-btn"
            onClick={onToggleCollapse}
            title="Collapse panel"
          >
            <LeftOutlined />
          </button>
        </div>
      </div>

      <div className="panel-filters">
        <div className="filters-header" onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
          <span>Filters & Search</span>
          <button className="toggle-filters-btn" type="button">
            {filtersCollapsed ? <DownOutlined /> : <UpOutlined />}
          </button>
        </div>

        {!filtersCollapsed && (
          <div className="filters-content">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by keyword..."
                value={searchText}
                onChange={(e) => onSearchTextChange(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Type</label>
              <select
                className="form-select"
                value={filterType}
                onChange={(e) => onFilterTypeChange(e.target.value)}
              >
                <option value="scheduled">Scheduled</option>
                <option value="instant">Instant</option>
                <option value="all">All</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => onFilterStatusChange(e.target.value)}
              >
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
                <option value="failed">Failed</option>
                <option value="opened">Opened</option>
                <option value="cleared">Cleared</option>
              </select>
            </div>

            <div className="filter-group">
              <button
                className="btn btn-primary btn-sm w-100"
                type="button"
                onClick={onDownloadAllUnopened}
                title="Download all green results from completed searches as CSV and mark them opened"
              >
                ⬇ Download All Unopened as CSV (green results only)
              </button>
            </div>

            <div className="filter-group">
              <button
                className="btn btn-danger btn-sm w-100"
                type="button"
                onClick={onDeleteAll}
                title="Delete all searches matching current filters"
              >
                <DeleteOutlined style={{ marginRight: 6 }} />
                Delete All Filtered
              </button>
            </div>

            {isAdmin && (
              <div className="filter-group">
                <label>User</label>
                <select
                  className="form-select"
                  value={filterUser}
                  onChange={(e) => onFilterUserChange(e.target.value)}
                >
                  <option value="all">All Users</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isAdmin && (
              <div className="filter-group">
                <button
                  className="btn btn-warning btn-sm w-100"
                  onClick={() => onPauseAll(searches)}
                  title="Pause all visible searches"
                >
                  <PauseCircleOutlined style={{ marginRight: 6 }} />
                  Pause All Visible
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="btn btn-sm btn-outline-secondary"
            title="First page"
          >
            <DoubleLeftOutlined />
          </button>
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn btn-sm btn-outline-secondary"
            title="Previous page"
          >
            <LeftOutlined />
          </button>
          <span className="pagination-page-info">
            {currentPage}/{totalPages}
          </span>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn btn-sm btn-outline-secondary"
            title="Next page"
          >
            <RightOutlined />
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="btn btn-sm btn-outline-secondary"
            title="Last page"
          >
            <DoubleRightOutlined />
          </button>
        </div>
      )}

      <div className="panel-list">
        {searches.length === 0 ? (
          <div className="no-searches">No searches found</div>
        ) : (
          searches.map((search) => (
            <div
              key={search.id}
              className={`search-item ${selectedSearch?.id === search.id ? 'selected' : ''}`}
              onClick={() => onSearchSelect(search)}
            >
              <div className="search-item-header">
                <span className="search-keyword" title={search.keyword || search.seller_id}>
                  {search.keyword || search.seller_id}
                </span>
                <span className={`badge ${getStatusBadgeClass(search.state)}`}>
                  {getStatusDisplayText(search.state)}
                </span>
              </div>

              <div className="search-item-tags">
                <span className="tag" title="Country">{search.country_id}</span>
                <span className="tag tag-schedule" title="Schedule">
                  {search.schedule == null ? 'Instant' : search.schedule === 1 ? 'Every day' : `Every ${search.schedule}d`}
                </span>
                <span className="tag" title="Number of pages">{search.number_of_pages}p</span>
                <span className="tag" title={`Last run: ${formatDate(search.last_run_at)}`}>
                  {getTimeAgo(search.last_run_at)}
                </span>
                <span className="tag tag-results" title="Total products">
                  {search.total_results ?? 0}
                </span>
                {search.schedule != null && (
                  <button
                    className="tag-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePause(search.id, search.paused);
                    }}
                    title={search.paused ? 'Resume search' : 'Pause search'}
                  >
                    {search.paused
                      ? <PlayCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                      : <PauseCircleOutlined style={{ fontSize: 16, color: '#faad14' }} />}
                  </button>
                )}
                <button
                  className="tag-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearchClear(search.id);
                  }}
                  title="Clear results"
                >
                  <ClearOutlined style={{ fontSize: 16 }} />
                </button>
                <button
                  className="tag-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearchDelete(search.id);
                  }}
                  title="Delete search"
                >
                  <DeleteOutlined style={{ fontSize: 16 }} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SearchesPanel;
